import {
  GidType,
  parseGid,
  toGid,
} from "@redotech/redo-shopify-common/gid-utils";
import {
  ExtensionShopifyClient,
  ShopifyCart,
  ShopifyCartAttributes,
  ShopifyCartItem,
  ShopifyClientType,
  ShopifyProduct,
  ShopifyProductJS,
  ShopifyShop,
} from "@redotech/shopify-client/ajax";
import {
  StorefrontApiClient,
  createStorefrontApiClient,
} from "@shopify/storefront-api-client";
import { createContext } from "react";
import { log } from "./monitoring/log";
import {
  ProductByHandleQueryVariables,
  ProductQueryVariables,
} from "./shopify-storefront.graphql";

export const ExtensionShopifyClientContext = createContext<
  ExtensionShopifyClient | undefined
>(undefined);

export class TapcartClient implements ExtensionShopifyClient {
  type = ShopifyClientType.TAPCART;
  storefrontClient: StorefrontApiClient;
  tapcart: Tapcart;
  private storefrontAccessToken: string;
  private storeUrl: string;

  constructor(
    storefrontAccessToken: string,
    storeUrl: string,
    tapcart: Tapcart,
  ) {
    this.storefrontClient = createStorefrontApiClient({
      storeDomain: `https://${storeUrl}`,
      apiVersion: "2026-07",
      publicAccessToken: storefrontAccessToken,
      retries: 3,
    });
    this.tapcart = tapcart;
    this.storefrontAccessToken = storefrontAccessToken;
    this.storeUrl = storeUrl;
  }

  async removeItemFromCart(
    item: ShopifyCartItem,
    numToRemove: number,
    signal?: AbortSignal,
  ): Promise<undefined> {
    await this.tapcart.actions.removeFromCart({
      lineItems: [
        { variantId: `${item.id}`, quantity: numToRemove, attributes: null },
      ],
    });
    return undefined;
  }

  async updateCartAttribute(
    attributes: ShopifyCartAttributes,
    signal?: AbortSignal | undefined,
  ) {
    this.tapcart.actions.updateCartAttributes(attributes);
  }

  async updateCartItemSellingPlan(
    item: ShopifyCartItem,
    sellingPlanId: number | null,
    signal?: AbortSignal | undefined,
  ): Promise<undefined> {
    throw new Error("Selling plan editing not supported on Tapcart");
  }

  async cartAdd(
    data: { items: Partial<ShopifyCartItem>[] },
    params: URLSearchParams,
  ): Promise<undefined> {
    await this.tapcart.actions.addToCart({
      lineItems: [
        {
          variantId: data.items[0]?.id?.toString(),
          quantity: data.items[0]?.quantity,
        },
      ],
    });
    return undefined;
  }

  async cartGet(signal?: AbortSignal | undefined): Promise<ShopifyCart> {
    log("[TapcartClient.cartGet] Starting cart retrieval");

    const cart = this.tapcart.variables?.cart;
    if (!cart) {
      log("[TapcartClient.cartGet] No cart data found in tapcart variables");
      return { currency: "USD", items: [] };
    }

    log(
      `[TapcartClient.cartGet] Found cart with ${cart.items?.length || 0} items, currency: ${cart.currency || "USD"}`,
    );

    const cartItems: ShopifyCartItem[] = [];
    const query = /* GraphQL */ `
      query product($id: ID!) {
        product(id: $id) {
          handle
          vendor
          title
          variants(first: 250) {
            edges {
              node {
                id
                price {
                  amount
                }
              }
            }
          }
        }
      }
    `;

    log(
      `[TapcartClient.cartGet] Processing ${cart.items.length} cart items with GraphQL queries`,
    );

    await Promise.all(
      cart.items.map(async (item, index) => {
        const variables: ProductQueryVariables = {
          id: toGid(GidType.PRODUCT, item.productId),
        };

        log(
          `[TapcartClient.cartGet] Executing GraphQL query for item ${index + 1}/${cart.items.length}, productId: ${item.productId}, variantId: ${item.variantId}, variables:`,
          variables,
        );

        const response = await fetch(
          `https://${this.storeUrl}/api/2026-07/graphql.json`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Shopify-Storefront-Access-Token": this.storefrontAccessToken,
            },
            body: JSON.stringify({ query, variables }),
            signal,
          },
        );

        if (!response.ok) {
          const error = new Error(`HTTP error! status: ${response.status}`);
          log(
            `[TapcartClient.cartGet] HTTP error for item ${index + 1}, productId: ${item.productId}, status: ${response.status}`,
          );
          throw error;
        }

        const result = await response.json();

        log(
          `[TapcartClient.cartGet] GraphQL request completed for item ${index + 1}, response:`,
          JSON.stringify(result),
        );

        let price = 0;
        if (result.data?.product) {
          const variant = result.data.product.variants.edges.find((edge: any) =>
            edge.node.id.includes(item.variantId.toString()),
          );
          price = variant ? parseFloat(variant.node.price.amount) : 0;

          log(
            `[TapcartClient.cartGet] Found product data for item ${index + 1}: ${result.data.product.title}, variant price: ${price}`,
          );

          cartItems.push({
            id: parseInt(item.variantId),
            product_id: parseInt(item.productId),
            variant_id: parseInt(item.variantId),
            handle: result.data.product.handle,
            quantity: item.quantity,
            vendor: result.data.product.vendor,
            title: result.data.product.title,
            requires_shipping: true,
            line_price: price,
          });
        } else {
          log(
            `[TapcartClient.cartGet] No product data found for item ${index + 1}, productId: ${item.productId}, response:`,
            JSON.stringify(result),
          );
        }
      }),
    );

    const finalCart = { currency: cart?.currency || "USD", items: cartItems };
    log(
      `[TapcartClient.cartGet] Cart retrieval completed successfully with ${cartItems.length} processed items:`,
      JSON.stringify(finalCart),
    );

    return finalCart;
  }

  async cartClear(): Promise<undefined> {
    for (const item of this.tapcart.variables?.cart?.items ?? []) {
      await this.tapcart.actions.removeFromCart({
        variantId: item.variantId,
        quantity: item.quantity,
      });
    }
    return undefined;
  }

  async getCollections(
    handle?: string,
    signal?: AbortSignal,
  ): Promise<{ title: string; handle: string }[] | undefined> {
    return;
  }

  async productGet(
    handle: string,
    signal?: AbortSignal | undefined,
  ): Promise<ShopifyProduct> {
    log(`[TapcartClient.productGet] Starting request for handle: ${handle}`);

    const query = /* GraphQL */ `
      query productByHandle($handle: String!) {
        product(handle: $handle) {
          id
          tags
          title
          variants(first: 250) {
            edges {
              node {
                id
                price {
                  amount
                }
                requiresShipping
              }
            }
          }
        }
      }
    `;

    const variables: ProductByHandleQueryVariables = { handle };
    log(
      "[TapcartClient.productGet] Executing GraphQL query with variables:",
      variables,
    );

    const response = await fetch(
      `https://${this.storeUrl}/api/2026-07/graphql.json`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": this.storefrontAccessToken,
        },
        body: JSON.stringify({ query, variables }),
        signal,
      },
    );

    if (!response.ok) {
      const error = new Error(`HTTP error! status: ${response.status}`);
      log(
        `[TapcartClient.productGet] HTTP error for handle: ${handle}, status: ${response.status}`,
      );
      throw error;
    }

    const result = await response.json();

    log(
      `[TapcartClient.productGet] GraphQL request completed with response: ${JSON.stringify(result)}`,
    );

    if (!result.data?.product) {
      const error = new Error("Product not found");
      log(
        `[TapcartClient.productGet] Product not found for handle: ${handle}, response: ${JSON.stringify(result)}`,
      );
      throw error;
    }

    const product = {
      id: +parseGid(result.data.product.id),
      tags: result.data.product.tags.join(", "),
      title: result.data.product.title,
      variants: result.data.product.variants.edges.map((edge: any) => ({
        id: +parseGid(edge.node.id),
        requires_shipping: edge.node.requiresShipping,
        price: parseFloat(edge.node.price.amount),
      })),
    };

    log(
      "[TapcartClient.productGet] Product retrieval completed successfully:",
      JSON.stringify(product),
    );

    return product;
  }

  async productGetJS(
    handle: string,
    signal?: AbortSignal | undefined,
  ): Promise<ShopifyProductJS> {
    log(`[TapcartClient.productGetJS] Starting request for handle: ${handle}`);

    const query = /* GraphQL */ `
      query productGetJs($handle: String!) {
        product(handle: $handle) {
          id
          priceRange {
            minVariantPrice {
              amount
            }
            maxVariantPrice {
              amount
            }
          }
          variants(first: 250) {
            edges {
              node {
                id
                price {
                  amount
                }
                requiresShipping
              }
            }
          }
        }
      }
    `;
    const variables = { handle };
    log(
      "[TapcartClient.productGetJS] Executing GraphQL query with variables:",
      variables,
    );
    const response = await fetch(
      `https://${this.storeUrl}/api/2026-07/graphql.json`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": this.storefrontAccessToken,
        },
        body: JSON.stringify({ query, variables }),
        signal,
      },
    );

    if (!response.ok) {
      const error = new Error(`HTTP error! status: ${response.status}`);
      log(
        `[TapcartClient.productGetJS] HTTP error for handle: ${handle}, status: ${response.status}`,
      );
      throw error;
    }

    const result = await response.json();

    log(
      `[TapcartClient.productGetJS] GraphQL request completed with response: ${JSON.stringify(result)}`,
    );

    if (!result.data?.product) {
      const error = new Error("Product not found");
      log(
        `[TapcartClient.productGetJS] Product not found for handle: ${handle}, response: ${JSON.stringify(result)}`,
      );
      throw error;
    }
    return {
      id: +parseGid(result.data.product.id),
      price_min: +result.data.product.priceRange.minVariantPrice.amount,
      price_max: +result.data.product.priceRange.maxVariantPrice.amount,
      variants: result.data.product.variants.edges.map((edge: any) => ({
        id: +parseGid(edge.node.id),
        price: +edge.node.price.amount,
        requires_shipping: edge.node.requiresShipping,
      })),
    };
  }

  async getShopMetafield(): Promise<ShopifyShop | undefined> {
    return;
  }

  async deleteCartAttribute(): Promise<void> {}
}

export function shopifyProductHandle(url: string) {
  if (!isOnProductPage()) {
    return "";
  }
  const u = new URL(url);
  // Need to handle both cases:
  // `/products/breeze-blazer/`
  // `/products/breeze-blazer`
  const pathname = u.pathname.endsWith("/")
    ? u.pathname.slice(0, -1)
    : u.pathname;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return decodeURIComponent(pathname.split("/").pop()!);
}

export function isOnProductPage(onTapcart: boolean | undefined = false) {
  return onTapcart
    ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      !!Tapcart!.variables?.product
    : location.href.includes("/products/");
}

export function isOnCollectionPage(onTapcart: boolean | undefined = false) {
  return onTapcart
    ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      !!Tapcart!.variables.collection
    : location.href.includes("/collections/");
}

export function isOnHomePage() {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return (globalThis as any).ShopifyAnalytics?.meta?.page?.pageType === "home";
}

export function isOnCartPage(onTapcart: boolean | undefined = false) {
  return onTapcart
    ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      !!Tapcart!.variables.cart
    : location.href.includes("/cart");
}
